import { IconButton } from '@material-ui/core';
import { CheckCircle, Error, MoreHoriz, PauseCircleFilled } from '@material-ui/icons';
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import Spin from 'components/Spin';
import Truncate from 'components/Truncate';
import React from 'react';
import { dateFormat } from 'v2/formatters/date';

import { InvoiceStatus, SubscriptionStatus } from '../interfaces';
import styles from '../styles';
import { useWebhookHistoryContext } from './context';
import { QuickView } from './Quickview';

import type { WebhookHistory } from '../interfaces';

export const ListWebhookHistory = () => {
  const classes = styles();
  const [openQuickview, setOpenQuickview] = React.useState<boolean>(false);
  const [currentWebhookHistory, setCurrentWebhookHistory] = React.useState<WebhookHistory>();

  const { listWebhookHistory, loading, type, productId, pagination, setPagination } = useWebhookHistoryContext();

  if (loading) {
    return <Spin />;
  }

  if (listWebhookHistory?.length === 0) {
    return (
      <Typography className={classes.notFound} color='inherit'>
        Nenhum histórico de webhook encontrado
      </Typography>
    );
  }

  const showProductColumn = productId === -1;
  return (
    <>
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              {type !== 'abandonment' && <TableCell>Fatura/Contrato</TableCell>}
              {showProductColumn && <TableCell>Produto</TableCell>}
              <TableCell>Comprador</TableCell>
              <TableCell>Data de envio</TableCell>
              <TableCell>Retorno HTTP</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listWebhookHistory?.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>Nenhum histórico encontrado</TableCell>
              </TableRow>
            )}
            {listWebhookHistory?.map((item) => {
              const error = item.state !== 'success';
              const pending = item.state === 'pending';
              const success = !error && !pending;
              const tooltipMessage = error ? 'Ocorreu um erro ao enviar webhook' : pending ? 'Aguardando para ser enviado' : 'Enviado com sucesso';
              return (
                <TableRow key={item._id} className={error ? classes.rowError : null}>
                  <TableCell>
                    <Tooltip title={tooltipMessage}>
                      <span>
                        {pending && <PauseCircleFilled color='disabled' />}
                        {error && <Error color='error' />}
                        {success && <CheckCircle color='secondary' />}
                      </span>
                    </Tooltip>
                  </TableCell>
                  {item.type === 'invoice' && (
                    <TableCell>
                      <div>{item.entityId}</div>
                      <div className={classes.invoice}>
                        <span>{InvoiceStatus[item.entityStatus] ?? '-'}</span>
                      </div>
                    </TableCell>
                  )}

                  {item.type === 'contract' && (
                    <TableCell>
                      <div>{item.entityId}</div>
                      <div className={`${classes.subscription} ${item.entityStatus}`}>
                        <span>{SubscriptionStatus[item.entityStatus]}</span>
                      </div>
                    </TableCell>
                  )}

                  {showProductColumn && (
                    <TableCell>
                      <Truncate count={50} final='...' title={item.product.title}>
                        {item.product.id !== -1 && `(${item.product.id}) ${item.product.title}`}
                      </Truncate>
                    </TableCell>
                  )}
                  <TableCell>
                    <Truncate count={25} final='...' title={item.customer.name}>
                      {item.customer.name}
                    </Truncate>
                    {item.customer.email}
                  </TableCell>
                  <TableCell>{dateFormat(item.updated_at, 'dd/MM/yyyy HH:mm:ss') || '-'}</TableCell>
                  <TableCell align='center'>{item.lastSend?.returnStatus || '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        setOpenQuickview(true);
                        setCurrentWebhookHistory(item);
                      }}
                    >
                      <MoreHoriz />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 40]}
          component='div'
          size='small'
          labelRowsPerPage='Itens por página:'
          count={pagination?.total_items || 0}
          rowsPerPage={pagination?.per_page || 10}
          page={(pagination?.current_page || 1) - 1}
          onPageChange={(_: unknown, page: number) => setPagination({ ...pagination, ...{ current_page: page + 1 } })}
          onRowsPerPageChange={(e) => setPagination({ ...pagination, ...{ per_page: Number(e.target?.value), current_page: 1 } })}
        />
      </TableContainer>
      <QuickView open={openQuickview} webhook={currentWebhookHistory} handleClose={() => setOpenQuickview(false)} />
    </>
  );
};
